import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { getStorefrontConfigPreview } from 'api/jalapeno';
import { ProductCardProvider } from 'features/shop/ProductCardV2/ProductCardProvider';
import ProductsModal, { useProductsModal } from 'features/shop/ProductsModal';
import { Alert } from 'honeydew/Alert';
import Loader from 'honeydew/Loader';
import Modal from 'honeydew/Modal';
import Slices from 'pages/shop/StorefrontV2/Slices';
import { linkStorefrontBannerCarouselsToProductCarousels } from 'utils/bannerCarouselLinkUtils';

import { BannerLinkErrorAlert } from './BannerLinkErrorAlert';

/**
 * Renders a Storefront Configuration (Lettuce Tool) preview.
 * @returns {JSX.Element}
 */
function StorefrontConfigPreview() {
    const { storefrontConfigurationID } = useParams();
    const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
    const { handleCloseProductsModal, productsModalData, productsModalOpen } = useProductsModal();

    const { data, isLoading } = useQuery({
        queryKey: ['storefront-config-preview', storefrontConfigurationID],
        queryFn: async () => {
            const response = await getStorefrontConfigPreview({ storefrontConfigurationID });

            if (response?.data?.carousels) {
                linkStorefrontBannerCarouselsToProductCarousels({
                    carousels: response.data.carousels,
                    logContext: { configID: response.data?.debug?.id },
                });
            }

            return response;
        },
    });

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <Loader />
            </div>
        );
    }

    const slices = data?.data?.carousels || [];

    if (!slices || slices.length === 0) {
        const apiErrorMessage = data.msg !== 'Success' ? data.msg : data.data.msg;

        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <Alert variant="error">{apiErrorMessage || 'Error loading storefront configuration'}</Alert>
            </div>
        );
    }

    return (
        <main className="min-h-[calc(100vh-80px) mt-70 bg-white md:mt-30">
            <div className="mt-10 overflow-x-hidden bg-malt px-10 md:bg-white md:pt-80 lg:mx-auto lg:max-w-(--breakpoint-3xl) if:bg-oat md:if:bg-white">
                <div className="z-10 mb-10 space-y-10 md:mb-20">
                    <Alert>
                        You are previewing the <span className="font-grotesk-bold">{data?.data?.debug?.name}</span>{' '}
                        storefront configuration.{' '}
                        <button
                            type="button"
                            onClick={() => setIsDebugModalOpen(true)}
                            aria-label="Storefront configuration details"
                        >
                            <InformationCircleIcon className="inline-block size-15" />
                        </button>
                    </Alert>
                    <BannerLinkErrorAlert carousels={slices} />
                    {isDebugModalOpen && (
                        <Modal
                            headline={
                                <h2 className="text-body-lg font-grotesk-bold">Storefront configuration details</h2>
                            }
                            open={isDebugModalOpen}
                            onClose={() => setIsDebugModalOpen(false)}
                        >
                            <div className="p-10">
                                <pre className="text-body-xs rounded-lg bg-[#fafafa] p-10">
                                    {JSON.stringify(data?.data?.debug, null, 2)}
                                </pre>
                            </div>
                        </Modal>
                    )}
                </div>
                <ProductCardProvider>
                    <Slices data={{ slices, isLoading, debug: data?.data?.debug }} />
                    {productsModalOpen && productsModalData && (
                        <ProductsModal
                            data={productsModalData.data}
                            deepLinkProductID={productsModalData.deepLinkProductID}
                            isOpen={productsModalOpen}
                            onClose={handleCloseProductsModal}
                            dsSourceOverride={productsModalData.dsSourceOverride}
                            productCardMetaData={productsModalData.productCardMetaData || {}}
                        />
                    )}
                </ProductCardProvider>
            </div>
        </main>
    );
}

export { StorefrontConfigPreview as Component };
